import { defineField, defineType } from 'sanity'
import { strongEmLink } from '../../data/richText'

export default defineType({
  title: 'Hero',
  name: 'hero',
  type: 'object',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    {
      name: 'background',
      title: 'Background',
    },
    {
      name: 'desktop',
      title: 'Desktop Settings',
    },
    {
      name: 'mobile',
      title: 'Mobile Settings',
    },
    {
      name: 'actions',
      title: 'Actions',
    },
  ],
  fields: [
    defineField({
      title: 'Hidden?',
      name: 'hidden',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      title: 'Internal Identifier',
      name: 'internalName',
      type: 'string',
      description:
        'Internal label used only in Sanity to distinguish between heroes with the same title. Not displayed on the frontend.',
      group: 'content',
    }),
    defineField({
      title: 'Title',
      name: 'name',
      type: 'text',
      rows: 3,
      group: 'content',
    }),
    defineField({
      title: 'Heading level',
      name: 'headingLevel',
      type: 'number',
      options: {
        list: [
          { title: 'H1', value: 1 },
          { title: 'H2', value: 2 },
        ],
      },
      description: 'Choose the heading level for the title. (default: H2)',
      initialValue: 2,
      group: 'content',
    }),
    defineField({
      title: 'Content',
      name: 'content',
      type: 'array',
      of: strongEmLink,
      group: 'content',
    }),
    defineField({
      title: 'Desktop BG',
      name: 'bgDesktop',
      type: 'image',
      description:
        'Object must be left aligned covering the maximum of 50% of the image width',
      options: {
        hotspot: true,
      },
      group: 'background',
    }),
    defineField({
      title: 'Mobile BG',
      name: 'bgMobile',
      type: 'image',
      options: {
        hotspot: true,
      },
      description:
        'Object must be bottom aligned covering the maximum of 50% of the image height',
      group: 'background',
    }),
    defineField({
      title: 'Background Video (Desktop)',
      name: 'bgVideoDesktop',
      type: 'mux.video',
      description:
        'Background video for desktop. Will replace the background images when provided. Videos should be optimized for web and not too long to ensure good performance.',
      options: {
        collapsible: true,
      },
      group: 'background',
    }),
    defineField({
      title: 'Background Video (Mobile)',
      name: 'bgVideoMobile',
      type: 'mux.video',
      description:
        'Background video for mobile devices. If not provided, the desktop video will be used on mobile as well.',
      options: {
        collapsible: true,
      },
      group: 'background',
    }),
    defineField({
      title: 'Show text over video',
      name: 'showTextOverVideo',
      type: 'boolean',
      description:
        'When enabled, the title, content, and buttons will be displayed as an overlay on top of the background video.',
      initialValue: false,
      group: 'background',
      hidden: ({parent}) => !parent?.bgVideoDesktop?.asset,
    }),
    defineField({
      title: 'Theme',
      name: 'theme',
      type: 'string',
      options: {
        list: [
          { title: 'Default (white bg, dark text)', value: 'default' },
          { title: 'Dark (dark bg, white text)', value: 'dark' },
          { title: 'Brand (red bg, white text)', value: 'brand' },
        ],
      },
      initialValue: 'default',
      description: 'Background & text colors.',
      group: 'desktop',
    }),
    defineField({
      title: 'Text Alignment',
      name: 'align',
      type: 'string',
      options: {
        list: [
          { title: 'Left', value: 'left' },
          { title: 'Center', value: 'center' },
          { title: 'Right', value: 'right' },
        ],
      },
      initialValue: 'left',
      description: 'Controls the alignment (justification) of text content.',
      group: 'desktop',
    }),
    defineField({
      title: 'Box Alignment',
      name: 'boxAlign',
      type: 'string',
      options: {
        list: [
          { title: 'Left', value: 'left' },
          { title: 'Center', value: 'center' },
          { title: 'Right', value: 'right' },
        ],
      },
      initialValue: 'left',
      description:
        'Controls the position of the content box within the hero, but only if a width other than "Full" is selected. (default: Left)',
      group: 'desktop',
    }),
    defineField({
      title: 'Vertical spacing',
      name: 'vSpacing',
      type: 'string',
      options: {
        list: [
          { title: 'Small', value: 'small' },
          { title: 'Medium', value: 'medium' },
          { title: 'Large', value: 'large' },
          { title: 'Extra Large', value: 'xLarge' },
        ],
      },
      initialValue: 'small',
      description: 'Adjust the vertical padding of the hero section.',
      group: 'desktop',
    }),
    defineField({
      title: 'Content width',
      name: 'width',
      type: 'string',
      options: {
        list: [
          { title: 'Full', value: 'full' },
          { title: '1/2', value: '1/2' },
          { title: '2/3', value: '2/3' },
          { title: '3/4', value: '3/4' },
        ],
      },
      initialValue: 'full',
      description: 'Limit the width of the content box within the hero section.',
      group: 'desktop',
    }),
    defineField({
      title: 'Minimal Height',
      name: 'minHeight',
      type: 'object',
      fields: [
        defineField({
          title: 'Value',
          name: 'value',
          type: 'number',
          validation: (rule) => rule.positive(),
          description: 'Enter a positive number',
        }),
        defineField({
          title: 'Unit',
          name: 'unit',
          type: 'string',
          validation: (rule) =>
            rule.custom((unit, context) => {
              if (context.parent?.value && !unit) {
                return 'Unit is required when a value is provided'
              }
              return true
            }),
          options: {
            list: [
              { title: 'Pixels (px)', value: 'px' },
              { title: 'Viewport Height (vh)', value: 'vh' },
            ],
            layout: 'radio',
            direction: 'horizontal',
          },
        }),
      ],
      options: {
        collapsible: false,
      },
      group: 'desktop',
    }),
    defineField({
      title: 'Vertical Alignment',
      name: 'verticalAlign',
      type: 'string',
      options: {
        list: [
          { title: 'Top', value: 'top' },
          { title: 'Center', value: 'center' },
          { title: 'Bottom', value: 'bottom' },
        ],
      },
      initialValue: 'top',
      description:
        'Controls the vertical positioning of content when min-height is set.',
      group: 'desktop',
    }),
    defineField({
      title: 'Overlay opacity',
      name: 'overlayOpacity',
      type: 'number',
      initialValue: 0,
      description: 'Opacity of the overlay over the image background.',
      validation: (rule) => rule.min(0).max(70),
      options: {
        list: [0, 10, 20, 30, 40, 50, 60, 70].map((value) => ({
          title: value.toString(),
          value,
        })),
      },
      group: 'desktop',
    }),
    defineField({
      title: 'Theme',
      name: 'themeMobile',
      type: 'string',
      options: {
        list: [
          { title: 'Default (white bg, dark text)', value: 'default' },
          { title: 'Dark (dark bg, white text)', value: 'dark' },
          { title: 'Brand (red bg, white text)', value: 'brand' },
        ],
      },
      initialValue: 'default',
      description: 'Background & text colors',
      group: 'mobile',
    }),
    defineField({
      title: 'Text Alignment',
      name: 'alignMobile',
      type: 'string',
      options: {
        list: [
          { title: 'Left', value: 'left' },
          { title: 'Center', value: 'center' },
          { title: 'Right', value: 'right' },
        ],
      },
      initialValue: 'left',
      description:
        'Controls the alignment (justification) of text content. (default: Left)',
      group: 'mobile',
    }),
    defineField({
      title: 'Smaller Mobile Heading',
      name: 'smallerMobileHeading',
      type: 'boolean',
      description:
        'When using long words in the heading, this option reduces the font size on mobile devices to prevent the layout from breaking and a horizontal scrollbar appearing.',
      group: 'mobile',
    }),
    defineField({
      title: 'Vertical spacing',
      name: 'vSpacingMobile',
      type: 'string',
      options: {
        list: [
          { title: 'Small', value: 'small' },
          { title: 'Medium', value: 'medium' },
          { title: 'Large', value: 'large' },
        ],
      },
      initialValue: 'small',
      description:
        'Adjust the vertical padding (top and bottom space) of the hero section. (default: Small)',
      group: 'mobile',
    }),
    defineField({
      title: 'Content width',
      name: 'widthMobile',
      type: 'string',
      options: {
        list: [
          { title: 'Full', value: 'full' },
          { title: '1/2', value: '1/2' },
          { title: '2/3', value: '2/3' },
          { title: '3/4', value: '3/4' },
        ],
      },
      initialValue: 'full',
      description:
        'Limit the width of the content box within the hero section. (default: Full)',
      group: 'mobile',
    }),
    defineField({
      title: 'Minimal height',
      name: 'minHeightMobile',
      type: 'object',
      fields: [
        defineField({
          title: 'Value',
          name: 'value',
          type: 'number',
          validation: (rule) => rule.positive(),
          description: 'Enter a positive number',
        }),
        defineField({
          title: 'Unit',
          name: 'unit',
          type: 'string',
          validation: (rule) =>
            rule.custom((unit, context) => {
              if (context.parent?.value && !unit) {
                return 'Unit is required when a value is provided'
              }
              return true
            }),
          options: {
            list: [
              { title: 'Pixels (px)', value: 'px' },
              { title: 'Viewport Height (vh)', value: 'vh' },
            ],
            layout: 'radio',
            direction: 'horizontal',
          },
        }),
      ],
      group: 'mobile',
      options: {
        collapsible: false,
      },
    }),
    defineField({
      title: 'Vertical Alignment Mobile',
      name: 'verticalAlignMobile',
      type: 'string',
      options: {
        list: [
          { title: 'Top', value: 'top' },
          { title: 'Center', value: 'center' },
          { title: 'Bottom', value: 'bottom' },
        ],
      },
      initialValue: 'top',
      description:
        'Controls the vertical positioning of content on mobile when min-height is set.',
      group: 'mobile',
    }),
    defineField({
      title: 'Overlay opacity',
      name: 'overlayOpacityMobile',
      type: 'number',
      initialValue: 0,
      description: 'Opacity of overlay over image background. (default: 0)',
      validation: (rule) => rule.min(0).max(70),
      options: {
        list: [0, 10, 20, 30, 40, 50, 60, 70].map((value) => ({
          title: value.toString(),
          value,
        })),
      },
      group: 'mobile',
    }),
    defineField({
      name: 'separateContentMobile',
      title: 'Separate content',
      type: 'boolean',
      description: 'Content will get separated from the image/heading on mobile',
      initialValue: false,
      group: 'mobile',
    }),
    defineField({
      name: 'buttons',
      title: 'Buttons',
      type: 'array',
      of: [
        {
          type: 'link',
        },
      ],
      group: 'actions',
    }),
    defineField({
      title: 'Hero button',
      type: 'heroButton',
      name: 'heroButton',
      description: 'Deprecated! Use the Buttons array field instead.',
      group: 'actions',
    }),
    defineField({
      title: 'Button',
      type: 'link',
      name: 'button',
      description: 'Deprecated! Use the Buttons array field instead.',
      group: 'actions',
    }),
  ],
  preview: {
    select: {
      title: 'name',
      internalName: 'internalName',
    },
    prepare({ title, internalName }) {
      return {
        title: internalName || title,
        subtitle: 'Hero',
      }
    },
  },
})